import { noop } from "lodash-es";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { windowFromRelative } from "@/math/coordinate-transform";
import { relativeFromViewport, scrollTo } from "@/math/coordinate-utils";
import type { ZoomedRectangle } from "@/model/board";
import type { RelativeCoordinate, ViewportCoordinate } from "@/model/coordinates";
import { asReadonly } from "@/utils/vue";

interface Scroller {
  scroll: (rect: ZoomedRectangle) => void;
}

export const useZoomStore = defineStore(
  "zoom",
  () => {
    const zooming = ref(false);
    const dropdownOpen = ref(false);
    const useZoomLayer = ref(true);
    const factor = ref(1); // only updated at the end of zooming
    const dynamicFactor = ref(1); // also updated during zooming
    let scroller: Scroller = { scroll: noop };

    const zoomingWithLayer = computed(() => zooming.value && useZoomLayer.value);

    function updateFactor() {
      factor.value = round(dynamicFactor.value);
    }

    function setDynamicFactor(factor: number, exact = false) {
      dynamicFactor.value = exact ? factor : round(factor);
    }

    return {
      zooming: asReadonly(zooming),
      dropdownOpen: asReadonly(dropdownOpen),
      useZoomLayer: asReadonly(useZoomLayer),
      factor: asReadonly(factor),
      dynamicFactor: asReadonly(dynamicFactor),

      zoomingWithLayer,

      scroll: (rect: ZoomedRectangle) => scroller.scroll(rect),
      setDropdownOpen: (open: boolean) => (dropdownOpen.value = open),
      setUseZoomLayer: (use: boolean) => (useZoomLayer.value = use),
      startZoom: (scroll: Scroller) => {
        scroller = scroll;
        zooming.value = true;
      },
      endZoom: () => (zooming.value = false),
      setDynamicFactor,
      updateFactor,
      setFactors: (factor: number) => {
        setDynamicFactor(factor);
        updateFactor();
      },
    };
  },
  { autoReset: true },
);

// scroll so that the viewport coordinate matches the initial relative coordinate
export class TargetScroller {
  viewport: ViewportCoordinate;
  relative: RelativeCoordinate;
  constructor(target: ViewportCoordinate) {
    this.viewport = target;
    this.relative = relativeFromViewport(target);
  }

  scroll(rect: ZoomedRectangle) {
    const windowCoord = windowFromRelative(this.relative, { board: rect });
    scrollTo(windowCoord, this.viewport);
  }
}

function round(zoom: number) {
  return Math.round(100 * zoom) / 100;
}
